import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { FileText, AlertTriangle, Construction, MapPin, Clock, Filter, Inbox } from 'lucide-react';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  verified: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  in_progress: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

export default function MyReportsPage() {
  const { authAxios, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    authAxios.get('/reports/my').then(r => {
      setReports(r.data.reports || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user, authLoading, authAxios, navigate]);

  const filtered = typeFilter === 'all' ? reports : reports.filter(r => r.report_type === typeFilter);

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8" data-testid="my-reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">My Reports</h1>
            <p className="text-sm text-muted-foreground">{reports.length} total reports submitted</p>
          </div>
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40" data-testid="my-reports-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Reports</SelectItem>
            <SelectItem value="violation">Violations</SelectItem>
            <SelectItem value="blackspot">Blackspots</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground">Loading reports...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 space-y-4" data-testid="no-reports">
          <Inbox className="w-12 h-12 text-muted-foreground mx-auto" />
          <p className="text-muted-foreground">No reports yet. Start reporting to help improve road safety!</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/report/violation')} className="gap-2" data-testid="report-violation-link">
              <AlertTriangle className="w-4 h-4" />Report Violation
            </Button>
            <Button variant="outline" onClick={() => navigate('/report/blackspot')} className="gap-2" data-testid="report-blackspot-link">
              <Construction className="w-4 h-4" />Report Blackspot
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((report, i) => (
            <Card key={report.id} className="hover:shadow-md transition-shadow duration-200 animate-fade-in-up" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`report-card-${report.id}`}>
              <CardContent className="p-4 md:p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      {report.report_type === 'violation' ? (
                        <AlertTriangle className="w-4 h-4 text-accent flex-shrink-0" />
                      ) : (
                        <Construction className="w-4 h-4 text-[hsl(var(--warning))] flex-shrink-0" />
                      )}
                      <span className="font-bold text-sm">{report.category}</span>
                      <Badge variant="outline" className="text-xs capitalize">{report.report_type}</Badge>
                    </div>
                    {report.description && (
                      <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{report.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      {report.vehicle_number && (
                        <span className="font-mono bg-muted px-2 py-0.5 rounded">{report.vehicle_number}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {report.latitude?.toFixed(4)}, {report.longitude?.toFixed(4)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(report.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[report.status] || statusColors.pending}`}>
                      {report.status?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                {report.media_url && (
                  <div className="mt-3">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${report.media_url}`}
                      alt="Evidence"
                      className="w-full max-h-48 object-cover rounded-lg"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
